import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ProgressIndicator from "@/components/signup/ProgressIndicator";
import StepClubSearch from "@/components/signup/StepClubSearch";
import StepBranding from "@/components/signup/StepBranding";
import StepTeamsPricing from "@/components/signup/StepTeamsPricing";
import StepPreview from "@/components/signup/StepPreview";
import StepTelegram from "@/components/signup/StepTelegram";
import StepAccount from "@/components/signup/StepAccount";
import {
  INITIAL_WIZARD_STATE,
  STORAGE_KEY,
  defaultTeamTag,
  type WizardState,
} from "@/components/signup/types";
import type { TierId } from "@/components/PricingTiers";

const TOTAL_STEPS = 6;

function loadState(): WizardState {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const legacyDesigns: Record<string, string> = {
        klassik: "1",
        klassisch: "1",
        rein: "2",
        modern: "2",
        stadion: "3",
        blitz: "4",
        bold: "4",
      };
      const savedDesign = String(parsed.designId || INITIAL_WIZARD_STATE.designId);
      const designId = legacyDesigns[savedDesign.toLowerCase()] ?? savedDesign;
      return {
        ...INITIAL_WIZARD_STATE,
        ...parsed,
        teams: Array.isArray(parsed.teams) ? parsed.teams : [],
        clubTeams: Array.isArray(parsed.clubTeams) ? parsed.clubTeams : [],
        designId,
        password: "",
      };
    }
  } catch {}
  return { ...INITIAL_WIZARD_STATE };
}

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const requestedTier = searchParams.get("paket");
  const initialTier: TierId = requestedTier === "s" || requestedTier === "m" || requestedTier === "l" || requestedTier === "jugend" ? requestedTier : "m";
  const [state, setState] = useState<WizardState>(() => ({ ...loadState(), selectedTier: initialTier }));
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const { password, ...rest } = state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
  }, [state]);

  useEffect(() => {
    if (requestedTier === "s" || requestedTier === "m" || requestedTier === "l" || requestedTier === "jugend") {
      setState((previous) => ({ ...previous, selectedTier: requestedTier }));
    }
  }, [requestedTier]);

  const updateState = (updates: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  const goNext = (updates?: Partial<WizardState>) => {
    if (updates) updateState(updates);
    const instagram = updates?.instagramEnabled ?? state.instagramEnabled;
    setStep((s) => {
      // Zustellung (Telegram) überspringen, wenn Instagram Auto-Posting gewählt ist
      if (s === 4 && instagram) return 6;
      return Math.min(s + 1, TOTAL_STEPS);
    });
  };

  const goBack = () =>
    setStep((s) => {
      if (s === 6 && state.instagramEnabled) return 4;
      return Math.max(s - 1, 1);
    });

  const handleAccountSubmit = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
          data: {
            club_name: state.club?.clubName,
          },
        },
      });

      if (error) {
        toast({
          title: "Registrierung fehlgeschlagen",
          description: error.message.includes("already registered")
            ? "Diese E-Mail ist bereits registriert. Bitte melde dich an."
            : error.message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const userId = data.user?.id;
      if (!userId) {
        toast({ title: "Fehler", description: "Account konnte nicht erstellt werden.", variant: "destructive" });
        setLoading(false);
        return;
      }

      // If telegram was already verified before registration, fetch chat_id
      let telegramChatId: string | null = null;
      if (state.telegramCode) {
        const { data: tvc } = await supabase
          .from("telegram_verification_codes")
          .select("chat_id")
          .eq("code", state.telegramCode)
          .maybeSingle();
        telegramChatId = tvc?.chat_id ?? null;
      }

      // Resolve internal bfv_clubs UUID from the BFV permanent id (which is not a UUID)
      let bfvClubUuid: string | null = null;
      if (state.club?.clubPermanentId) {
        const { data: bfvRow } = await supabase
          .from("bfv_clubs")
          .select("id")
          .eq("bfv_id", String(state.club.clubPermanentId))
          .maybeSingle();
        bfvClubUuid = bfvRow?.id ?? null;
      }

      const mainTeam = state.teams[0] ?? null;

      // Insert club row
      const { error: clubError } = await supabase.from("clubs").insert({
        user_id: userId,
        club_name: state.club?.clubName ?? "Mein Verein",
        bfv_club_id: bfvClubUuid,
        liga: mainTeam?.category ?? "Unbekannt",
        team_name: mainTeam?.teamName ?? "1. Mannschaft",
        team_type: mainTeam?.teamName ?? "1. Mannschaft",
        bfv_url: mainTeam?.teamUrl ?? null,
        logo_url: state.branding.logoDataUrl || state.club?.logoUrl || null,
        primary_color: state.branding.primaryColor,
        design_template: Number(state.designId) || 1,
        selected_design: state.designId || "1",
        selected_tier: state.selectedTier,
        youth_pack: state.youthPack,
        instagram_enabled: state.instagramEnabled,
        telegram_code: state.telegramCode || crypto.randomUUID().slice(0, 8),
        telegram_chat_id: telegramChatId,
        is_active: true,
      });

      if (clubError) {
        console.error("Club insert error:", clubError);
        toast({
          title: "Verein konnte nicht angelegt werden",
          description: clubError.message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      updateState({ email });

      // Alle ausgewählten Mannschaften speichern
      const teamRows = state.teams.map((t) => ({
        account_id: userId,
        bfv_team_id: t.teamPermanentId,
        team_name: t.teamName,
        team_tag: t.tag || defaultTeamTag(t.teamName),
        active: true,
      }));

      if (teamRows.length > 0) {
        const { error: teamsError } = await supabase.from("teams").insert(teamRows);
        if (teamsError) console.warn("Teams insert failed:", teamsError);
      }

      // Einmalig: Anmeldung an Torflash-Backend melden (serverseitig, mit Geheimnis)
      let setupPending = false;
      try {
        const { data: signupRes, error: signupErr } = await supabase.functions.invoke("club-signup", { body: {} });
        if (signupErr || !signupRes?.ok) {
          setupPending = true;
          console.error("club-signup failed:", signupErr ?? signupRes);
        }
      } catch (e) {
        setupPending = true;
        console.error("club-signup failed:", e);
      }
      if (setupPending) {
        toast({
          title: "Anmeldung gespeichert",
          description: "Die Einrichtung läuft – wir melden uns.",
        });
      }

      // Fire-and-forget: sync club info to Google Sheets
      supabase.functions
        .invoke("sync-to-sheets", {
          body: {
            clubName: state.club?.clubName,
            teamName: mainTeam?.teamName,
            bfvUrl: mainTeam?.teamUrl,
            primaryColor: state.branding.primaryColor,
            secondaryColor: state.branding.secondaryColor,
            email,
            selectedTier: state.selectedTier,
            youthPack: state.youthPack,
            instagramEnabled: state.instagramEnabled,
            teams: teamRows.map((t) => ({
              bfvTeamId: t.bfv_team_id,
              teamName: t.team_name,
              teamTag: t.team_tag,
              active: t.active,
            })),
          },
        })
        .then(({ error }) => {
          if (error) console.warn("Sheets sync failed:", error);
        });

      toast({
        title: "Willkommen bei Torflash!",
        description: "Dein Verein ist angelegt. Jetzt nur noch das Abo abschließen.",
      });
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem("torflash_selected_tier", state.selectedTier);
      localStorage.setItem("torflash_team_count", String(state.teams.length));
      localStorage.setItem("torflash_youth_pack", state.youthPack ? "1" : "0");
      localStorage.setItem("torflash_instagram", state.instagramEnabled ? "1" : "0");
      navigate("/checkout");
    } catch (err) {
      console.error(err);
      toast({ title: "Unerwarteter Fehler", description: String(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background py-10">
      <div className={`container ${step === 3 ? "max-w-6xl" : "max-w-3xl"}`}>
        <ProgressIndicator currentStep={step} totalSteps={TOTAL_STEPS} />

        <div className={`glass-card rounded-xl ${step === 3 ? "p-3 sm:p-6 md:p-10" : "p-6 md:p-10"}`}>
          {step === 1 && <StepClubSearch state={state} onNext={(u) => goNext(u)} />}
          {step === 2 && <StepBranding state={state} onNext={(u) => goNext(u)} onBack={goBack} />}
          {step === 3 && <StepPreview state={state} onNext={(u) => goNext(u)} onBack={goBack} />}
          {step === 4 && <StepTeamsPricing state={state} onNext={(u) => goNext(u)} onBack={goBack} />}
          {step === 5 && <StepTelegram state={state} onNext={(u) => goNext(u)} onBack={goBack} />}
          {step === 6 && (
            <StepAccount state={state} onSubmit={handleAccountSubmit} onBack={goBack} loading={loading} />
          )}
        </div>

        <p className="mt-4 text-center text-sm text-muted-foreground">
          Bereits registriert?{" "}
          <Link to="/login" className="text-primary hover:underline">
            Einloggen →
          </Link>
        </p>
      </div>
    </div>
  );
}
